// Inventory Tab for POS - Branch-level inventory management
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Package, AlertTriangle, ArrowRight, RefreshCw, History, Plus, Minus, Truck } from 'lucide-react';
import { API } from './constants';

export function InventoryTab({ branchId, getAuthHeaders, userRole }) {
  const [branchInventory, setBranchInventory] = useState([]);
  const [masterStock, setMasterStock] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [transferQuantity, setTransferQuantity] = useState(1);

  const canManageInventory = ['master_admin', 'admin'].includes(userRole);

  useEffect(() => {
    if (branchId) {
      fetchBranchInventory();
      fetchMasterStock();
    }
  }, [branchId]);

  const fetchBranchInventory = async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inventory/branch/${branchId}`, getAuthHeaders());
      setBranchInventory(response.data);
    } catch (error) {
      // error handled
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterStock = async () => {
    try {
      const response = await axios.get(`${API}/inventory/master-stock`, getAuthHeaders());
      setMasterStock(response.data);
    } catch (error) {
      // error handled
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await axios.get(`${API}/inventory/transfers?branch_id=${branchId}&limit=20`, getAuthHeaders());
      setTransferHistory(response.data);
    } catch (error) {
      // error handled
    }
  };

  const handleTransfer = async () => {
    if (!selectedProduct || transferQuantity <= 0) {
      toast.error('Selecciona un producto y cantidad válida');
      return;
    }

    try {
      const response = await axios.post(`${API}/inventory/transfer`, {
        product_id: selectedProduct,
        branch_id: branchId,
        quantity: transferQuantity
      }, getAuthHeaders());

      toast.success(response.data.message);
      setShowTransferDialog(false);
      setSelectedProduct('');
      setTransferQuantity(1);
      fetchBranchInventory();
      fetchMasterStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al transferir inventario');
    }
  };

  const handleAdjust = async (inventoryId, delta) => {
    try {
      await axios.put(`${API}/inventory/${inventoryId}/adjust`, {
        quantity: delta
      }, getAuthHeaders());
      
      toast.success(`Stock ${delta > 0 ? 'aumentado' : 'reducido'}`);
      fetchBranchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar inventario');
    }
  };

  const openHistoryDialog = () => {
    fetchTransferHistory();
    setShowHistoryDialog(true);
  };

  const lowStockItems = branchInventory.filter(item => item.stock <= item.min_stock);
  const selectedProductData = masterStock.find(p => p.id === selectedProduct);

  return (
    <div className="space-y-6" data-testid="inventory-tab">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-gold" />
          <h2 className="text-lg font-semibold text-white">Inventario de Sucursal</h2>
          <Badge variant="outline" className="text-zinc-400 border-zinc-700">
            {branchInventory.length} productos
          </Badge>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchBranchInventory}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="inventory-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={openHistoryDialog}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="inventory-history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            Historial
          </Button>
          {canManageInventory && (
            <Button 
              size="sm" 
              onClick={() => setShowTransferDialog(true)}
              className="bg-gold text-black hover:bg-gold/90"
              data-testid="inventory-transfer-btn"
            >
              <Truck className="w-4 h-4 mr-2" />
              Transferir Stock
            </Button>
          )}
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <Card className="bg-red-950/30 border-red-900 p-4" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 text-red-400 mb-3">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">Stock Bajo ({lowStockItems.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.map(item => (
              <Badge key={item.id} variant="destructive" className="text-sm">
                {item.product_name}: {item.stock} unidades
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Inventory Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gold" />
        </div>
      ) : branchInventory.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center">
          <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
          <p className="text-zinc-400">No hay inventario en esta sucursal</p>
          {canManageInventory && (
            <Button 
              className="mt-4 bg-gold text-black hover:bg-gold/90"
              onClick={() => setShowTransferDialog(true)}
            >
              <Truck className="w-4 h-4 mr-2" />
              Transferir Productos
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {branchInventory.map(item => (
            <Card 
              key={item.id} 
              className={`bg-zinc-900 border-zinc-800 p-4 ${
                item.stock <= item.min_stock ? 'border-red-900/50' : ''
              }`}
              data-testid={`inventory-item-${item.product_id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-white">{item.product_name}</h3>
                  <p className="text-sm text-zinc-500">${item.price.toFixed(2)}</p>
                </div>
                {item.stock <= item.min_stock && (
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                )}
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">{item.stock}</p>
                  <p className="text-xs text-zinc-500">Mín: {item.min_stock}</p>
                </div>
                
                {canManageInventory && (
                  <div className="flex gap-1">
                    <Button 
                      size="icon" 
                      variant="outline"
                      className="h-8 w-8 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                      onClick={() => handleAdjust(item.id, -1)}
                      data-testid={`inventory-minus-${item.product_id}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="outline"
                      className="h-8 w-8 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                      onClick={() => handleAdjust(item.id, 1)}
                      data-testid={`inventory-plus-${item.product_id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              {item.last_transfer_date && (
                <p className="text-xs text-zinc-600 mt-2">
                  Última transferencia: {new Date(item.last_transfer_date).toLocaleDateString('es-MX')}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-h-[90vh] overflow-y-auto" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-gold" />
              Transferir Stock a Sucursal
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Producto</label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="transfer-product-select">
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {masterStock.filter(p => p.stock > 0).map(product => (
                    <SelectItem 
                      key={product.id} 
                      value={product.id}
                      className="text-white hover:bg-zinc-700"
                    >
                      {product.name} (Stock: {product.stock})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Cantidad</label>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  onClick={() => setTransferQuantity(Math.max(1, transferQuantity - 1))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input 
                  type="number" 
                  value={transferQuantity}
                  onChange={(e) => setTransferQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="bg-zinc-800 border-zinc-700 text-white text-center w-20"
                  min="1"
                  max={selectedProductData?.stock || 999}
                  data-testid="transfer-quantity-input"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  onClick={() => setTransferQuantity(Math.min(selectedProductData?.stock || 999, transferQuantity + 1))}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {selectedProductData && (
                <p className="text-xs text-zinc-500 mt-1">
                  Disponible en almacén: {selectedProductData.stock} unidades
                </p>
              )}
            </div>
            
            {selectedProduct && selectedProductData && (
              <Card className="bg-zinc-800 border-zinc-700 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Almacén Central</span>
                  <ArrowRight className="w-4 h-4 text-gold" />
                  <span className="text-zinc-400">Sucursal</span>
                </div>
                <div className="flex items-center justify-between mt-2 font-semibold">
                  <span className="text-white">{selectedProductData.stock - transferQuantity}</span>
                  <span className="text-gold">+{transferQuantity}</span>
                  <span className="text-white">
                    {(branchInventory.find(i => i.product_id === selectedProduct)?.stock || 0) + transferQuantity}
                  </span>
                </div>
              </Card>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowTransferDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleTransfer}
              className="bg-gold text-black hover:bg-gold/90"
              disabled={!selectedProduct || transferQuantity <= 0}
              data-testid="confirm-transfer-btn"
            >
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gold" />
              Historial de Transferencias
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-2 py-4">
            {transferHistory.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">No hay transferencias registradas</p>
            ) : (
              transferHistory.map(transfer => (
                <Card key={transfer.id} className="bg-zinc-800 border-zinc-700 p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-white">{transfer.product_name}</p>
                      <p className="text-sm text-zinc-400">
                        {transfer.transfer_type === 'adjustment' ? 'Ajuste' : 'Transferencia'}: {transfer.quantity > 0 ? '+' : ''}{transfer.quantity} unidades
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">
                        {new Date(transfer.created_at).toLocaleDateString('es-MX')}
                      </p>
                      <p className="text-xs text-zinc-600">
                        {transfer.transferred_by_name}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
